import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import PgIcon from './pg-icon';
import PgBusinessItem from './pg-business-item';

/**
 * Content of `PgSidebar`'s user-row dropdown: the scrollable business list
 * (fades at the top/bottom past 3 entries, same as pegment.css's
 * `.pg-ws-list.has-overflow`) plus the add/settings/help/logout action
 * rows below it. Pegment equivalent of this app's legacy
 * `BusinessMenuItems` (`app/components/layout/business-menu-items.js`) —
 * same role (dropdown list + footer actions), rebuilt on pegment.css's
 * `.pg-dropdown-*`/`.pg-ws-*` classes instead of `CardBox` + raw Tailwind.
 *
 * `PgSidebar` owns the floating-panel positioning and the Help & Support
 * submenu's open state — this component only needs `helpTriggerRef` (to
 * anchor that submenu) and `onHelpTriggerClick` (to open it) passed in.
 */
const PgSidebarBusinessMenu = ({
    businesses,
    onSelect,
    onAdd,
    settingsHref,
    onSettingsClick,
    helpTriggerRef,
    onHelpTriggerClick,
    onLogout,
    className,
}) => {
    const listRef = useRef(null);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [atBottom, setAtBottom] = useState(false);

    const updateFade = useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        setHasOverflow(el.scrollHeight > el.clientHeight + 1);
        setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
    }, []);

    useEffect(() => {
        updateFade();
    }, [businesses, updateFade]);

    const listClasses = [
        'pg-ws-list',
        hasOverflow && 'has-overflow',
        atBottom && 'at-bottom',
    ].filter(Boolean).join(' ');

    return (
        <div className={['pg-dropdown-content', className].filter(Boolean).join(' ')}>
            <div className={listClasses} ref={listRef} onScroll={updateFade}>
                {businesses.map((business) => (
                    <PgBusinessItem
                        key={business.id}
                        id={business.id}
                        name={business.name}
                        plan={business.plan}
                        active={business.active}
                        onClick={onSelect}
                    />
                ))}
            </div>

            <div className="pg-dropdown-action" onClick={onAdd}>
                <PgIcon name="plus" size={18} /> Add new business
            </div>

            <div className="pg-ws-divider" />

            {settingsHref ? (
                <Link href={settingsHref} className="pg-dropdown-action" onClick={onSettingsClick}>
                    <PgIcon name="settings-2" size={18} /> Settings
                </Link>
            ) : (
                <div className="pg-dropdown-action" onClick={onSettingsClick}>
                    <PgIcon name="settings-2" size={18} /> Settings
                </div>
            )}
            <div
                ref={helpTriggerRef}
                className="pg-dropdown-action has-submenu"
                onClick={onHelpTriggerClick}
            >
                <span><PgIcon name="lifebuoy" size={18} /> Help and Support</span>
                <PgIcon name="chevron-right" size={14} />
            </div>

            <div className="pg-ws-divider" />

            <div className="pg-dropdown-action has-submenu" onClick={onLogout}>
                <span><PgIcon name="logout" size={18} /> Log out</span>
                <PgIcon name="chevron-right" size={14} />
            </div>
        </div>
    );
};

PgSidebarBusinessMenu.propTypes = {
    /** `[{ id, name, plan, active }]`. */
    businesses: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        plan: PropTypes.string,
        active: PropTypes.bool,
    })),
    /** Called with a business's `id` when a row is clicked. */
    onSelect: PropTypes.func,
    onAdd: PropTypes.func,
    /** Renders "Settings" as a `next/link` to this path instead of a plain clickable row. */
    settingsHref: PropTypes.string,
    onSettingsClick: PropTypes.func,
    /** Ref for the "Help and Support" row — the parent anchors its submenu's floating panel to this node. */
    helpTriggerRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any }),
    ]),
    onHelpTriggerClick: PropTypes.func,
    onLogout: PropTypes.func,
    className: PropTypes.string,
};

PgSidebarBusinessMenu.defaultProps = {
    businesses: [],
    onSelect: undefined,
    onAdd: undefined,
    settingsHref: undefined,
    onSettingsClick: undefined,
    helpTriggerRef: undefined,
    onHelpTriggerClick: undefined,
    onLogout: undefined,
    className: '',
};

export default PgSidebarBusinessMenu;
